// @file Shared utilities for acquiring display streams and mixing audio via the Web Audio API.

interface MixedStreamResult {
  combinedStream: MediaStream
  micTrack: MediaStreamTrack | null
  videoAudioTrack: MediaStreamTrack | null
}

/**
 * Acquires a display media stream with shared constraints used by screen recorders.
 * Requests window-level capture with system audio enabled.
 */
async function getDisplayStream(): Promise<MediaStream> {
  return await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'window' },
    audio: true,
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
    systemAudio: 'include',
  } as any)
}

/**
 * Mixes display (system) audio and microphone audio into a single stream
 * using the Web Audio API. Returns the combined stream along with references
 * to the individual mic and system-audio tracks.
 */
function getMixedSystemAndMicAudio(displayStream: MediaStream, micStream: MediaStream): MixedStreamResult {
  const micTrack = micStream.getAudioTracks()[0]

  const audioContext = new AudioContext()
  // For Chrome, system audio is only shared when sharing a tab.
  // Some other browsers don't support sharing system audio entirely.
  const hasSystemAudio = displayStream.getAudioTracks().length > 0

  let videoAudioTrack: MediaStreamTrack | null = null
  let systemAudioSource: MediaStreamAudioSourceNode | undefined
  if (hasSystemAudio) {
    videoAudioTrack = displayStream.getAudioTracks()[0]
    systemAudioSource = audioContext.createMediaStreamSource(displayStream)
  }

  const micAudioSource = audioContext.createMediaStreamSource(micStream)
  const destination = audioContext.createMediaStreamDestination()

  if (hasSystemAudio) {
    systemAudioSource!.connect(destination)
  }
  micAudioSource.connect(destination)

  const videoTracks = displayStream.getVideoTracks()
  const mixedAudioTracks = destination.stream.getAudioTracks()
  const combinedStream = new MediaStream([...videoTracks, ...mixedAudioTracks])

  return { combinedStream, micTrack, videoAudioTrack }
}

export { getDisplayStream, getMixedSystemAndMicAudio }
export type { MixedStreamResult }
